// Hook: returns a click handler that, when a data point representing a day
// is clicked, narrows the qa-copilot filter to that single day and
// re-fetches the dashboard. Shows a toast confirming the narrow.
//
// The user can clear the date in the existing CopilotFilters bar.
import { useDispatch, useSelector } from 'react-redux'

import { toast } from '@/components/toasts/toast.helpers'
import { formatTime } from '@/utils/helpers'
import { setFilter } from '@/reducers/qa-copilot/qa-copilot.redux'
import { fetchCopilotDashboardData } from '@/reducers/qa-copilot/qa-copilot.actions'

// Parse a chart day value ('YYYY-MM-DD' preferred, legacy 'MM/DD')
// into a local midnight Date. MM/DD gets its year from the filter's
// endDate -- V8 defaults `new Date('06/03')` to 2001 otherwise.
const parseLocalDay = (day, fallbackYear) => {
  if (!day) {
    return null
  }
  const dayString = String(day)

  // 'YYYY-MM-DD' (or that prefix on a longer ISO string)
  let match = dayString.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) {
    const [, year, month, dayOfMonth] = match
    return new Date(Number(year), Number(month) - 1, Number(dayOfMonth))
  }

  // 'MM/DD' — BE's default `date_format='%m/%d'`
  match = dayString.match(/^(\d{1,2})\/(\d{1,2})$/)
  if (match) {
    const [, month, dayOfMonth] = match
    const year = fallbackYear || new Date().getFullYear()
    return new Date(year, Number(month) - 1, Number(dayOfMonth))
  }

  // Generic fallback for anything else parseable (ISO timestamps, etc.)
  const fallback = new Date(dayString)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

// Extract the year from the active filter's endDate (preferring endDate
// because that's the most recent day in the visible window).
const yearFromFilters = (filters) => {
  const raw = filters?.endDate || filters?.startDate
  if (!raw) {
    return null
  }
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear()
}

export const useDayClickFilter = () => {
  const dispatch = useDispatch()
  const filterOrgId = useSelector((state) => state.qaCopilot.filters?.organizationId)
  const currentUserOrgId = useSelector((state) => state.currentUser?.organizationid)
  const filters = useSelector((state) => state.qaCopilot.filters)

  return (day) => {
    const parsed = parseLocalDay(day, yearFromFilters(filters))
    if (!parsed) {
      return
    }

    // Match the string format used elsewhere in qa-copilot filters
    // (see CopilotFilters.handleUpdateRange / qa-copilot.redux initialState).
    const startDate = formatTime(parsed, 'yyyy-MM-DDT00:00:00Z')
    const endDate = formatTime(parsed, 'yyyy-MM-DDT23:59:59Z')

    dispatch(setFilter({ startDate, endDate }))
    // Existing filter bar uses an Apply button; for a click-to-drill UX
    // we want the data to refresh immediately.
    dispatch(fetchCopilotDashboardData(filterOrgId || currentUserOrgId))
    toast.success(`Filtered to ${formatTime(parsed, 'MMM D, YYYY')}`)
  }
}
